import { CSSProperties, useState } from 'react'

import { useOAuth2UrlLazyQuery } from 'graph/generated/payments/graphql-types'
import { replaceStateParamWithRedirectUrl } from 'utils/ory'
import { actionTracking } from 'utils/posthog'

import BaseButton from '@/ui/Button/v2'

/**
 * Starts the Hydra OAuth flow and lands the user on `redirectUrl` afterwards
 * (defaults to the current page). Shared by the "Sign in" button and by inline
 * sign-in links so both take the exact same path.
 */
export const useSignIn = (redirectUrl?: string) => {
  const [getOAuth2Url] = useOAuth2UrlLazyQuery()
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const signIn = async () => {
    setIsLoading(true)
    // Stays true only while we are actually navigating away; any early exit
    // (fetch error, missing or non-http URL) hands the control back.
    let navigating = false
    try {
      const url = await getOAuth2Url()
      const redirectTo =
        redirectUrl ?? `${window.location.pathname}${window.location.search}`

      const href = redirectTo
        ? replaceStateParamWithRedirectUrl(url?.data?.OAuth2Url, redirectTo)
        : url?.data?.OAuth2Url

      actionTracking({
        action: 'handle sign in',
        properties: {
          url: url,
          redirectTo: redirectTo,
          pathname: window.location.pathname,
          search: window.location.search,
          href: href,
          isLoading: isLoading
        }
      })

      // OAuth2Url from Hydra — validate protocol inline (see packages/utils/security/index.ts)
      if (!href) return
      const parsed = new URL(href)
      if (!['https:', 'http:'].includes(parsed.protocol)) return
      navigating = true
      window.location.href = href
    } catch {
      return
    } finally {
      if (!navigating) setIsLoading(false)
    }
  }

  return { signIn, isLoading }
}

const SignInButton = ({
  text,
  styles,
  redirectUrl
}: {
  text?: string
  redirectUrl?: string
  styles?: CSSProperties
}) => {
  const { signIn, isLoading } = useSignIn(redirectUrl)

  return (
    <BaseButton
      onClick={signIn}
      loading={isLoading}
      styles={{
        marginLeft: 'auto',
        width: '104px',
        height: '26px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: '12px',
        lineHeight: '18px',
        fontWeight: 700,
        ...styles
      }}
    >
      {text || 'Sign in'}
    </BaseButton>
  )
}

export default SignInButton
